import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './NFCPlates.css';
import API_URL from '../config';

const NFCPlates = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plates, setPlates] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('current_month');
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewCompanyName, setViewCompanyName] = useState(() => companyId ? sessionStorage.getItem(`rc_cname_${companyId}`) || '' : '');

  useEffect(() => {
    fetchPlates();
  }, [periodFilter, companyId]);

  useEffect(() => {
    if (!companyId) return;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/companies/${companyId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.name) { sessionStorage.setItem(`rc_cname_${companyId}`, data.name); setViewCompanyName(data.name); } })
      .catch(() => {});
  }, [companyId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        setPeriodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPlates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = new URL(`${API_URL}/api/nfc-plates/stats`);
      url.searchParams.append('period', periodFilter);
      if (companyId) url.searchParams.append('view_as_company_id', companyId);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlates(data);
        if (companyId && data.length > 0 && data[0].company_name) {
          sessionStorage.setItem(`rc_cname_${companyId}`, data[0].company_name);
          setViewCompanyName(data[0].company_name);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des plaques:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleExportXLSX = () => {
    setExporting(true);
    try {
      const periodLabel = {
        'current_month': '30 derniers jours',
        '3_months': '3 mois',
        '6_months': '6 mois',
        '1_year': '1 an',
        'all_time': 'Tout l\'historique',
      }[periodFilter] || periodFilter;

      const wb = XLSX.utils.book_new();

      const rows = [
        ['Nom de la plaque', 'UID', 'Période', 'Scans', 'Avis collectés', 'Note moyenne', 'Meilleures heures', 'Meilleur jour'],
        ...plates.map(p => [
          p.name || '',
          p.plate_uid || '',
          periodLabel,
          p.total_scans ?? 0,
          p.total_reviews ?? 0,
          p.average_rating > 0 ? p.average_rating : '-',
          p.best_hour || '-',
          p.best_day || '-',
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: 24 }, // Nom
        { wch: 16 }, // UID
        { wch: 20 }, // Période
        { wch: 10 }, // Scans
        { wch: 14 }, // Avis
        { wch: 14 }, // Note
        { wch: 20 }, // Heures
        { wch: 16 }, // Jour
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Plaques NFC');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `plaques_nfc_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Erreur export:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Bandeau admin vue entreprise */}
      {companyId && (
        <div className="admin-view-banner">
          <span className="admin-banner-label">Vous visualisez les plaques NFC de <strong>{viewCompanyName || `Entreprise #${companyId}`}</strong></span>
          <strong className="admin-banner-label-mobile">{viewCompanyName || `Entreprise #${companyId}`}</strong>
          <Link to={`/admin/company/${companyId}`} className="admin-view-banner-exit">
            <span className="admin-banner-exit-text">Quitter cette vue</span>
            <span>✕</span>
          </Link>
        </div>
      )}
      <div className={`dashboard${companyId ? ' dashboard--with-banner' : ''}`}>
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="hamburger-button" onClick={() => setMenuOpen(!menuOpen)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path d="M20 7L4 7" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              <path d="M20 12L4 12" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
              <path d="M20 17L4 17" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"></path>
            </g>
          </svg>
        </button>
        <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="mobile-logo" />
        <div className="mobile-spacer"></div>
      </div>

      <aside className={`sidebar ${menuOpen ? 'menu-open' : ''}`}>
        <div className="sidebar-mobile-header">
          <button className="close-menu-button" onClick={() => setMenuOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
              <path d="M6 6L18 18" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" className="sidebar-mobile-logo" />
          <div className="mobile-spacer"></div>
        </div>

        <Link to="/" className="sidebar-logo">
          <img src="https://risingcard.fr/wp-content/uploads/2024/12/Logo-rising-card.svg" alt="Rising Card" />
        </Link>

        <nav className="sidebar-nav">
          <Link to={companyId ? `/admin/company/${companyId}/dashboard` : '/dashboard'} className="nav-item">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.75 2C2.75 1.58579 2.41421 1.25 2 1.25C1.58579 1.25 1.25 1.58579 1.25 2V12.0574C1.24999 14.3658 1.24998 16.1748 1.43975 17.5863C1.63399 19.031 2.03933 20.1711 2.93414 21.0659C3.82895 21.9607 4.96897 22.366 6.41371 22.5603C7.82519 22.75 9.63423 22.75 11.9426 22.75H22C22.4142 22.75 22.75 22.4142 22.75 22C22.75 21.5858 22.4142 21.25 22 21.25H12C9.62178 21.25 7.91356 21.2484 6.61358 21.0736C5.33517 20.9018 4.56445 20.5749 3.9948 20.0052C3.42514 19.4355 3.09825 18.6648 2.92637 17.3864C2.75159 16.0864 2.75 14.3782 2.75 12V2Z" fill="black"/>
              <path d="M19.5875 7.46641C19.8451 7.14204 19.791 6.67026 19.4666 6.41267C19.1422 6.15508 18.6704 6.20921 18.4128 6.53359L15.2948 10.46C15.0496 10.7688 14.8887 10.9708 14.7561 11.1162C14.6265 11.2585 14.5657 11.2989 14.538 11.3137C14.3272 11.4264 14.0754 11.4319 13.8599 11.3285C13.8316 11.3149 13.7691 11.2772 13.6333 11.1407C13.4946 11.0011 13.3251 10.8063 13.0666 10.5085L13.0505 10.4898C12.8126 10.2157 12.6098 9.98186 12.4308 9.80184C12.2448 9.6147 12.0414 9.4401 11.7894 9.31918C11.143 9.00898 10.3875 9.02541 9.75518 9.36342C9.50872 9.49518 9.31307 9.67845 9.13536 9.87351C8.96441 10.0612 8.77192 10.3036 8.54619 10.5878L5.41267 14.5336C5.15508 14.8579 5.20921 15.3297 5.53358 15.5873C5.85795 15.8449 6.32973 15.7908 6.58733 15.4664L9.70551 11.54C9.95077 11.2311 10.1116 11.0292 10.2442 10.8837C10.3738 10.7414 10.4347 10.7011 10.4623 10.6863C10.6731 10.5736 10.925 10.5681 11.1404 10.6715C11.1687 10.6851 11.2313 10.7228 11.367 10.8593C11.5057 10.9989 11.6752 11.1936 11.9337 11.4915L11.9498 11.5101C12.1877 11.7843 12.3905 12.0181 12.5695 12.1981C12.7555 12.3853 12.9589 12.5599 13.2109 12.6808C13.8573 12.991 14.6129 12.9746 15.2452 12.6365C15.4916 12.5048 15.6873 12.3215 15.865 12.1264C16.0359 11.9388 16.2284 11.6964 16.4541 11.4121L19.5875 7.46641Z" fill="black"/>
            </svg>
            <span>Statistiques</span>
          </Link>
          <Link to={companyId ? `/admin/company/${companyId}/reviews` : '/reviews'} className="nav-item">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M11.292 3.30809C10.8982 3.82224 10.4538 4.61551 9.80827 5.77355L9.48057 6.36141C9.46082 6.39684 9.44132 6.43194 9.42201 6.4667C9.12127 7.0079 8.86745 7.46469 8.45844 7.77518C8.04503 8.08901 7.54106 8.20227 6.95535 8.3339C6.91771 8.34235 6.87973 8.35089 6.84141 8.35956L6.20506 8.50354C4.94974 8.78757 4.09575 8.98299 3.51082 9.21351C2.94002 9.43845 2.81953 9.62275 2.77361 9.77044C2.72514 9.9263 2.7237 10.1647 3.06494 10.7068C3.41129 11.257 3.99558 11.9432 4.85011 12.9425L5.28393 13.4498C5.30915 13.4793 5.33413 13.5084 5.35886 13.5373C5.76187 14.0074 6.09791 14.3993 6.25205 14.895C6.40526 15.3877 6.35448 15.9054 6.29291 16.5331C6.28913 16.5716 6.28532 16.6105 6.2815 16.6499L6.21591 17.3267C6.08682 18.6589 5.99978 19.5762 6.02922 20.2369C6.05859 20.8965 6.1979 21.0788 6.30865 21.1628C6.40716 21.2376 6.58925 21.3204 7.18248 21.1504C7.78535 20.9777 8.58659 20.6111 9.76202 20.0699L10.3578 19.7956C10.3945 19.7787 10.4309 19.7619 10.4669 19.7452C11.0117 19.4934 11.4843 19.2748 12 19.2748C12.5157 19.2748 12.9883 19.4934 13.5331 19.7452C13.5692 19.7619 13.6055 19.7787 13.6422 19.7956L14.238 20.0699C15.4134 20.6111 16.2147 20.9777 16.8176 21.1504C17.4108 21.3204 17.5929 21.2376 17.6914 21.1628C17.8021 21.0788 17.9414 20.8965 17.9708 20.2369C18.0002 19.5762 17.9132 18.6589 17.7841 17.3267L17.7185 16.6499C17.7147 16.6105 17.7109 16.5716 17.7071 16.5331C17.6456 15.9054 17.5948 15.3877 17.748 14.895C17.9021 14.3993 18.2382 14.0074 18.6412 13.5372C18.6659 13.5084 18.6909 13.4793 18.7161 13.4498L19.1499 12.9425C20.0044 11.9432 20.5887 11.257 20.9351 10.7068C21.2763 10.1647 21.2749 9.9263 21.2264 9.77044C21.1805 9.62275 21.06 9.43845 20.4892 9.21351C19.9043 8.98299 19.0503 8.78757 17.795 8.50354L17.1586 8.35956C17.1203 8.35089 17.0823 8.34235 17.0447 8.33389C16.459 8.20227 15.955 8.08901 15.5416 7.77518C15.1326 7.46469 14.8788 7.0079 14.578 6.4667C14.5587 6.43194 14.5392 6.39684 14.5195 6.36141L14.1918 5.77355C13.5462 4.61551 13.1018 3.82224 12.7081 3.30809C12.3147 2.79443 12.1138 2.75 12 2.75C11.8863 2.75 11.6853 2.79443 11.292 3.30809ZM10.1011 2.3961C10.5777 1.77363 11.1669 1.25 12 1.25C12.8331 1.25 13.4223 1.77363 13.899 2.3961C14.3674 3.00773 14.864 3.89876 15.471 4.98776L15.8296 5.63106C16.2222 6.33523 16.3226 6.48482 16.4486 6.58044C16.5698 6.67247 16.7262 6.7238 17.4896 6.89654L18.1897 7.05492C19.3653 7.32088 20.3338 7.53999 21.0392 7.81796C21.7714 8.10651 22.4121 8.5318 22.6588 9.32502C22.9029 10.1101 22.6285 10.8323 22.2045 11.5059C21.7925 12.1604 21.1344 12.9298 20.3306 13.8698L19.8561 14.4247C19.3391 15.0292 19.2311 15.1772 19.1803 15.3404C19.1286 15.5069 19.1334 15.6992 19.2115 16.5052L19.2831 17.2433C19.4048 18.4994 19.5041 19.5236 19.4693 20.3037C19.434 21.0977 19.2536 21.8601 18.5984 22.3576C17.9308 22.8643 17.1542 22.8072 16.4044 22.5924C15.6774 22.3841 14.7711 21.9667 13.6705 21.46L13.0149 21.1581C12.2975 20.8278 12.1439 20.7748 12 20.7748C11.8561 20.7748 11.7025 20.8278 10.9852 21.1581L10.3295 21.46C9.22898 21.9667 8.32265 22.3841 7.59565 22.5924C6.84587 22.8072 6.0692 22.8643 5.40168 22.3576C4.7464 21.8601 4.56607 21.0977 4.5307 20.3037C4.49595 19.5236 4.59523 18.4993 4.71697 17.2433L4.7885 16.5052C4.8666 15.6992 4.87147 15.5069 4.81971 15.3404C4.76894 15.1772 4.66094 15.0292 4.14393 14.4247L3.66945 13.8698C2.8656 12.9299 2.20753 12.1604 1.79553 11.5059C1.37149 10.8323 1.09714 10.1101 1.34127 9.32502C1.58794 8.5318 2.22867 8.10651 2.96086 7.81796C3.66622 7.53999 4.63474 7.32088 5.81038 7.05492L5.87404 7.04052L6.51039 6.89654C7.27382 6.72381 7.43023 6.67247 7.55148 6.58044C7.67743 6.48482 7.77785 6.33523 8.17039 5.63106L8.52899 4.98775C9.13601 3.89876 9.63268 3.00773 10.1011 2.3961Z" fill="black"/>
            </svg>
            <span>Avis collectés</span>
          </Link>
          <Link to="/nfc-plates" className="nav-item active">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16.3939 2.02121L16.4604 2.03904C17.5598 2.33361 18.431 2.56704 19.1162 2.81458C19.8172 3.06779 20.3888 3.35744 20.8597 3.79847C21.5453 4.44068 22.0252 5.27179 22.2385 6.18671C22.385 6.81503 22.3501 7.45486 22.2189 8.18849C22.0906 8.90573 21.8572 9.77697 21.5626 10.8764L21.0271 12.8747C20.7326 13.974 20.4991 14.8452 20.2516 15.5305C19.9984 16.2314 19.7087 16.803 19.2677 17.2739C18.6459 17.9377 17.8471 18.4087 16.9665 18.6316C16.7093 19.2213 16.3336 19.7554 15.8597 20.1993C15.3888 20.6403 14.8172 20.9299 14.1162 21.1832C13.431 21.4307 12.5598 21.6641 11.4605 21.9587L11.394 21.9765C10.2946 22.2711 9.42337 22.5045 8.70613 22.6328C7.9725 22.764 7.33266 22.7989 6.70435 22.6524C5.78943 22.4391 4.95832 21.9592 4.31611 21.2736C3.87508 20.8027 3.58542 20.2311 3.33222 19.5302C3.08468 18.8449 2.85124 17.9737 2.55667 16.8743L2.02122 14.876C1.72664 13.7766 1.4932 12.9054 1.36495 12.1882C1.23376 11.4546 1.19881 10.8147 1.34531 10.1864C1.55864 9.27149 2.03849 8.44038 2.72417 7.79817C3.19505 7.35714 3.76664 7.06749 4.46758 6.81428C5.15283 6.56674 6.02404 6.3333 7.12341 6.03873L7.15665 6.02983C7.42112 5.95896 7.67134 5.89203 7.90825 5.82944C8.29986 4.43031 8.64448 3.44126 9.31611 2.72417C9.95832 2.03849 10.7894 1.55864 11.7043 1.34531C12.3327 1.19881 12.9725 1.23376 13.7061 1.36495C14.4233 1.49319 15.2945 1.72664 16.3939 2.02121ZM7.45502 7.5028C6.36214 7.79571 5.57905 8.00764 4.9772 8.22505C4.36778 8.4452 4.00995 8.64907 3.74955 8.89296C3.2804 9.33237 2.95209 9.90103 2.80613 10.527C2.72511 10.8745 2.72747 11.2863 2.84152 11.9242C2.95723 12.5712 3.17355 13.381 3.47902 14.521L3.99666 16.4529C4.30212 17.5929 4.51967 18.4023 4.74299 19.0205C4.96314 19.63 5.16701 19.9878 5.4109 20.2482C5.85031 20.7173 6.41897 21.0456 7.04496 21.1916C7.39242 21.2726 7.80425 21.2703 8.4421 21.1562C9.08915 21.0405 9.89893 20.8242 11.0389 20.5187C12.1789 20.2132 12.9884 19.9957 13.6066 19.7724C14.216 19.5522 14.5739 19.3484 14.8343 19.1045C14.9719 18.9756 15.0973 18.8357 15.2096 18.6865C15.0306 18.6612 14.8463 18.629 14.6557 18.5911C13.9839 18.4575 13.1769 18.2413 12.1808 17.9744L12.1234 17.959C11.024 17.6644 10.1528 17.431 9.46758 17.1835C8.76664 16.9302 8.19505 16.6406 7.72416 16.1996C7.03849 15.5574 6.55864 14.7262 6.34531 13.8113C6.19881 13.183 6.23376 12.5432 6.36494 11.8095C6.4932 11.0923 6.72664 10.2211 7.02122 9.12174L7.45502 7.5028ZM13.4421 2.84152C12.8042 2.72747 12.3924 2.72511 12.045 2.80613C11.419 2.95209 10.8503 3.2804 10.4109 3.74955C9.97479 4.21518 9.70642 4.93452 9.2397 6.64323C9.16384 6.92093 9.08365 7.22023 8.99665 7.54488L8.47902 9.47673C8.17355 10.6167 7.95723 11.4265 7.84152 12.0736C7.72747 12.7114 7.72511 13.1232 7.80613 13.4707C7.95209 14.0967 8.2804 14.6654 8.74955 15.1048C9.00995 15.3487 9.36778 15.5525 9.9772 15.7727C10.5954 15.996 11.4049 16.2136 12.5449 16.519C13.5703 16.7938 14.3303 16.997 14.9482 17.1199C15.5635 17.2422 15.981 17.2723 16.3232 17.23C16.3976 17.2209 16.4691 17.2082 16.5389 17.1919C17.1649 17.0459 17.7335 16.7176 18.1729 16.2485C18.4168 15.9881 18.6207 15.6303 18.8408 15.0208C19.0642 14.4026 19.2817 13.5932 19.5872 12.4532L20.1048 10.5213C20.4103 9.38129 20.6266 8.57151 20.7423 7.92446C20.8564 7.28661 20.8587 6.87479 20.7777 6.52733C20.6317 5.90133 20.3034 5.33267 19.8343 4.89327C19.5739 4.64937 19.216 4.4455 18.6066 4.22535C17.9884 4.00203 17.1789 3.78448 16.0389 3.47902C14.8989 3.17355 14.0892 2.95723 13.4421 2.84152Z" fill="black"/>
            </svg>
            <span>Mes plaques NFC</span>
          </Link>
          {!companyId && (
            <Link to="/google-business" className="nav-item">
              <svg width="24" height="24" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <span>Google Business</span>
            </Link>
          )}
        </nav>

        <div className="sidebar-user">
          <div className="user-avatar">
            <img 
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent((user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email?.split('@')[0]) || 'U')}&background=fbd900&color=000&bold=true`} 
              alt="Avatar" 
            />
          </div>
          <div className="user-info">
            <div className="user-name">
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user?.email?.split('@')[0] || 'Utilisateur'}
            </div>
            <div className="user-role">{user?.is_admin ? 'Administrateur' : user?.company_name || 'Entreprise'}</div>
          </div>
          <button className="logout-button" onClick={handleLogout} title="Déconnexion">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <div>
              <h1>Mes plaques NFC</h1>
              <p>Consultez les performances de vos plaques NFC</p>
            </div>
            <div className="header-filters">
              <div className="custom-dropdown">
                <button 
                  className="dropdown-trigger"
                  onClick={() => setPeriodDropdownOpen(!periodDropdownOpen)}
                >
                  <span>
                    {periodFilter === 'current_month' ? '30 derniers jours' :
                     periodFilter === '3_months' ? '3 mois' :
                     periodFilter === '6_months' ? '6 mois' :
                     periodFilter === '1_year' ? '1 an' : 'Tout l\'historique'}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {periodDropdownOpen && (
                  <div className="dropdown-menu">
                    <div 
                      className={`dropdown-item ${periodFilter === 'current_month' ? 'active' : ''}`}
                      onClick={() => {
                        setPeriodFilter('current_month');
                        setPeriodDropdownOpen(false);
                      }}
                    >
                      30 derniers jours
                    </div>
                    <div 
                      className={`dropdown-item ${periodFilter === '3_months' ? 'active' : ''}`}
                      onClick={() => {
                        setPeriodFilter('3_months');
                        setPeriodDropdownOpen(false);
                      }}
                    >
                      3 mois
                    </div>
                    <div 
                      className={`dropdown-item ${periodFilter === '6_months' ? 'active' : ''}`}
                      onClick={() => {
                        setPeriodFilter('6_months');
                        setPeriodDropdownOpen(false);
                      }}
                    >
                      6 mois
                    </div>
                    <div 
                      className={`dropdown-item ${periodFilter === '1_year' ? 'active' : ''}`}
                      onClick={() => {
                        setPeriodFilter('1_year');
                        setPeriodDropdownOpen(false);
                      }}
                    >
                      1 an
                    </div>
                    <div 
                      className={`dropdown-item ${periodFilter === 'all_time' ? 'active' : ''}`}
                      onClick={() => {
                        setPeriodFilter('all_time');
                        setPeriodDropdownOpen(false);
                      }}
                    >
                      Tout l'historique
                    </div>
                  </div>
                )}
              </div>

              <button className={`btn-export${exporting ? ' btn-loading' : ''}`} onClick={handleExportXLSX} disabled={exporting || loading || plates.length === 0}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {exporting ? 'Export...' : 'Exporter XLSX'}
              </button>
            </div>
          </div>
        </header>

        <div className="plates-content">
          {loading ? (
            <div className="plates-grid">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="plate-card skeleton-card">
                  <div className="plate-header">
                    <div className="plate-icon-skeleton skeleton-shimmer"></div>
                    <div className="skeleton-text skeleton-shimmer" style={{ width: '150px', height: '20px' }}></div>
                  </div>
                  
                  <div className="plate-details">
                    <div className="detail-row">
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '60px', height: '14px' }}></div>
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '40px', height: '18px' }}></div>
                    </div>
                    <div className="detail-row">
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '50px', height: '14px' }}></div>
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '30px', height: '18px' }}></div>
                    </div>
                    <div className="detail-row">
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '90px', height: '14px' }}></div>
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '35px', height: '18px' }}></div>
                    </div>
                    <div className="detail-row">
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '110px', height: '14px' }}></div>
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '50px', height: '18px' }}></div>
                    </div>
                    <div className="detail-row">
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '85px', height: '14px' }}></div>
                      <div className="skeleton-text skeleton-shimmer" style={{ width: '55px', height: '18px' }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : plates.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M16.3939 2.02121L16.4604 2.03904C17.5598 2.33361 18.431 2.56704 19.1162 2.81458C19.8172 3.06779 20.3888 3.35744 20.8597 3.79847C21.5453 4.44068 22.0252 5.27179 22.2385 6.18671C22.385 6.81503 22.3501 7.45486 22.2189 8.18849C22.0906 8.90573 21.8572 9.77697 21.5626 10.8764L21.0271 12.8747C20.7326 13.974 20.4991 14.8452 20.2516 15.5305C19.9984 16.2314 19.7087 16.803 19.2677 17.2739C18.6459 17.9377 17.8471 18.4087 16.9665 18.6316C16.7093 19.2213 16.3336 19.7554 15.8597 20.1993C15.3888 20.6403 14.8172 20.9299 14.1162 21.1832C13.431 21.4307 12.5598 21.6641 11.4605 21.9587L11.394 21.9765C10.2946 22.2711 9.42337 22.5045 8.70613 22.6328C7.9725 22.764 7.33266 22.7989 6.70435 22.6524C5.78943 22.4391 4.95832 21.9592 4.31611 21.2736C3.87508 20.8027 3.58542 20.2311 3.33222 19.5302C3.08468 18.8449 2.85124 17.9737 2.55667 16.8743L2.02122 14.876C1.72664 13.7766 1.4932 12.9054 1.36495 12.1882C1.23376 11.4546 1.19881 10.8147 1.34531 10.1864C1.55864 9.27149 2.03849 8.44038 2.72417 7.79817C3.19505 7.35714 3.76664 7.06749 4.46758 6.81428C5.15283 6.56674 6.02404 6.3333 7.12341 6.03873L7.15665 6.02983C7.42112 5.95896 7.67134 5.89203 7.90825 5.82944C8.29986 4.43031 8.64448 3.44126 9.31611 2.72417C9.95832 2.03849 10.7894 1.55864 11.7043 1.34531C12.3327 1.19881 12.9725 1.23376 13.7061 1.36495C14.4233 1.49319 15.2945 1.72664 16.3939 2.02121Z" fill="#e0e0e0"/>
              </svg>
              <h3>Aucune plaque NFC</h3>
              <p>Vos plaques NFC apparaîtront ici</p>
            </div>
          ) : (
            <div className="plates-grid">
              {plates.map((plate) => (
                <div key={plate.id} className="plate-card">
                  <div className="plate-header">
                    <div className="plate-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 8.32a7.43 7.43 0 0 1 0 7.36"/>
                        <path d="M9.46 6.21a11.76 11.76 0 0 1 0 11.58"/>
                        <path d="M12.91 4.1a15.91 15.91 0 0 1 .01 15.8"/>
                        <path d="M16.37 2a20.16 20.16 0 0 1 0 20"/>
                      </svg>
                    </div>
                    <div className="plate-header-text">
                      <h3>{plate.name}</h3>
                      {plate.plate_uid && (
                        <span className="plate-uid-badge">{plate.plate_uid}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="plate-details">
                    <div className="detail-row">
                      <span className="detail-label">Scans</span>
                      <span className="detail-value">{plate.total_scans}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Avis</span>
                      <span className="detail-value">{plate.total_reviews}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Note moyenne</span>
                      <span className="detail-value">{plate.average_rating > 0 ? plate.average_rating : '-'}</span>
                    </div>
                    {plate.best_hour && (
                      <div className="detail-row">
                        <span className="detail-label">Meilleures heures</span>
                        <span className="detail-value">{plate.best_hour}</span>
                      </div>
                    )}
                    {plate.best_day && (
                      <div className="detail-row">
                        <span className="detail-label">Meilleur jour</span>
                        <span className="detail-value">{plate.best_day}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
    </>
  );
};

export default NFCPlates;
